import { makeAutoObservable, toJS } from 'mobx';
import VideoCreatorStore, {
  KARAOKE_TRACK_NUMBER,
  VIDEO_DEFAULT_WIDTH,
} from '../VideoCreatorStore';
import { ElementState } from '../../renderer/ElementState';
import { AspectRatio } from '../../types.ts/video';
import { deepClone } from '../../utility/deepClone';
import { KaraokeConfig } from '@src/videoTranscriptionProcessor/types/karaokeTypes';
import { ExtraElementData } from '../../types.ts/story';
import { sizeAwareLog } from '@src/utility/datadog';
import { mapToElementState } from '@src/videoTranscriptionProcessor/utils';

// const TOTAL_DASHES_VERTICALLY = 36;
// const DASH_BREADTH = 3;

class ReframingModeManager {
  store: VideoCreatorStore;
  flags: Record<string, boolean> = {};

  actualVideoWidth: number | null = null;
  rectangleTrack: number | null = null;
  relevantElementSources: ElementState['source'][] = [];
  karaokeConfigToRestore: KaraokeConfig | null = null;

  isToggleAllowed: boolean = true;
  private _inCriticalSectionForEnter: boolean = false;
  private _inCriticalSectionForExit: boolean = false;

  constructor(store: VideoCreatorStore) {
    makeAutoObservable(this, {
      store: false,
      flags: false,
    });
    this.store = store;
  }

  public async toggleReframingMode() {
    if (!this.isToggleAllowed) {
      this.log('toggleReframingMode | not allowed');
      return;
    }
    const activeElement = this.findActiveFrameableElement();
    if (activeElement) {
      this.log('toggleReframingMode | enter', activeElement.source);
      await this.enterReframingMode();
    } else {
      this.log('toggleReframingMode | exit');
      await this.exitReframingMode();
    }
    this.log('toggleReframingMode | done');
  }

  public allowToggle(value: boolean) {
    this.log('allowToggle', value);
    this.isToggleAllowed = value;
  }

  private findActiveFrameableElement(): ElementState | undefined {
    if (
      (!this.flags.enableReframingModeIn16_9 &&
        this.store.currentVideo!.aspectRatio === AspectRatio.AR_16_9) ||
      this.containsLayoutElement()
    ) {
      return undefined;
    }
    const activeElement = this.store.getActiveElement();
    return activeElement && this.isFrameableElement(activeElement)
      ? activeElement
      : undefined;
  }

  private isFrameableElement(element: ElementState): boolean {
    return (
      this.store.isRegularImageElement(element) ||
      this.store.isVideoElement(element)
    );
  }

  private containsLayoutElement(): boolean {
    return !!this.store.renderer?.findElement((element) =>
      this.store.stackedLayoutManager.isLayoutElement(element),
    );
  }

  private isRelevantElement(element: ElementState): boolean {
    return (
      this.isFrameableElement(element) ||
      this.isTextElement(element) ||
      this.isLogoElement(element)
    );
  }

  private isTextElement(element: ElementState): boolean {
    return element.source.type === 'text';
  }

  private isKaraokeElement(
    element: ElementState | ElementState['source'],
  ): boolean {
    return element.track === KARAOKE_TRACK_NUMBER;
  }

  private isLogoElement(element: ElementState): boolean {
    return this.store.isLogoElement(element);
  }

  private getRelevantElementSources(): ElementState[] {
    return (this.store.rootStore.videoSourceStore.source.elements || []).filter(
      (elementSource: any) =>
        this.isRelevantElement(mapToElementState(elementSource)),
    );
  }

  private async getVideoSource(
    waitForNextRefresh: boolean = false,
  ): Promise<Record<string, any>> {
    const videoSourceStore = this.store.rootStore.videoSourceStore;
    if (waitForNextRefresh && videoSourceStore.hasDebouncedSourceRefresh) {
      this.log('getVideoSource | waiting for source refresh');
      await new Promise((resolve) =>
        videoSourceStore.onNextSourceRefresh(resolve),
      );
    }
    return videoSourceStore.source;
  }

  private async setVideoSource(videoSource: Record<string, any>) {
    await this.store.rootStore.videoSourceStore.setSource(videoSource);
  }

  private async assertDimensionsMatchAspectRatioCheck() {
    try {
      const validDimensions = this.store.getStandardDimensionsForAspectRatio(
        this.store.currentVideo!.aspectRatio,
      );
      const videoSource = await this.getVideoSource();

      if (
        videoSource.width !== validDimensions.width ||
        videoSource.height !== validDimensions.height
      ) {
        this.logError(
          'enterReframingMode | video dimensions do not match current aspect ratio',
          {
            actual: `${videoSource.width}x${videoSource.height}`,
            expected: `${validDimensions.width}x${validDimensions.height}`,
            aspectRatio: this.store.currentVideo!.aspectRatio,
          },
        );
        return false;
      }
      return true;
    } catch (err) {
      this.logError('enterReframingMode | error in assertDimensionsCheck', err);
      return false;
    }
  }

  private async enterReframingMode() {
    if (this.isModeActive()) {
      this.log('enterReframingMode | already active | done');
      return;
    }

    if (this._inCriticalSectionForEnter) {
      this.log('enterReframingMode | in critical section | skip');
      return;
    }

    this.log('enterReframingMode | entering critical section');
    this._inCriticalSectionForEnter = true;

    this.assertDimensionsMatchAspectRatioCheck();

    const videoSource = await this.getVideoSource(true);
    this.log('enterReframingMode | videoSource', videoSource);

    this.actualVideoWidth = videoSource.width;
    this.rectangleTrack = this.store.getMaxTrack() + 1;
    this.relevantElementSources = this.getRelevantElementSources();
    this.log('enterReframingMode | actualVideoWidth', this.actualVideoWidth);
    this.log(
      'enterReframingMode | relevantElementSources',
      this.relevantElementSources,
    );

    const videoSourceModified = this.modifyVideoSourceForEnter(videoSource);
    this.log('enterReframingMode | videoSourceModified', videoSourceModified);
    try {
      await this.setVideoSource(videoSourceModified);
    } catch (err) {
      this.actualVideoWidth = null;
      this.rectangleTrack = null;
      this.relevantElementSources = [];
      this.logError('enterReframingMode | setVideoSource error', err);
      await this.unselectActiveElement();
      return;
    } finally {
      this.log('enterReframingMode | leaving critical section');
      this._inCriticalSectionForEnter = false;
    }

    this.karaokeConfigToRestore =
      this.store.karaokeProducer!.getKaraokeConfig();
    this.log(
      'enterReframingMode | karaokeConfigToRestore',
      this.karaokeConfigToRestore,
    );

    const karaokeConfigModified = this.modifyKaraokeConfigForEnter(
      this.karaokeConfigToRestore,
    );
    this.log(
      'enterReframingMode | karaokeConfigModified',
      karaokeConfigModified,
    );
    this.store.karaokeProducer!.setConfig(karaokeConfigModified);

    this.log('enterReframingMode | done');
  }

  private modifyKaraokeConfigForEnter(karaokeConfig: KaraokeConfig) {
    if (this.isSimplified()) {
      this.log('modifyKaraokeConfigForEnter | simplified');
      return karaokeConfig;
    }
    this.log('modifyKaraokeConfigForEnter | regular');
    let newKaraokeConfigModifiable = {
      width: karaokeConfig.width,
      x: karaokeConfig.x,
    };
    this.scaleRelevantElementToReframingMode(
      newKaraokeConfigModifiable,
      karaokeConfig,
    );
    return { ...karaokeConfig, ...newKaraokeConfigModifiable };
  }

  public setKaraokeConfigToRestore(karaokeConfig: KaraokeConfig) {
    this.karaokeConfigToRestore = karaokeConfig;
  }

  public restoreKaraokeConfigForExit(
    karaokeConfig?: KaraokeConfig,
  ): KaraokeConfig {
    this.log(
      'restoreKaraokeConfigForExit | karaokeConfigToRestore',
      this.karaokeConfigToRestore,
    );
    this.log('restoreKaraokeConfigForExit | karaokeConfig', karaokeConfig);
    return this.karaokeConfigToRestore || karaokeConfig!;
  }

  private modifyVideoSourceForEnter(
    videoSourceInput: Record<string, any>,
  ): Record<string, any> {
    const videoSource = deepClone(videoSourceInput);

    // if (this.isSimplified()) {
    this.log('modifyVideoSourceForEnter | simplified');
    // this.insertPreviewRectanglesSimplified(videoSource, {
    //   time: 0,
    //   duration: this.store.duration,
    // });
    // } else {
    this.log('modifyVideoSourceForEnter | regular');
    videoSource.width = VIDEO_DEFAULT_WIDTH;
    const elements = videoSource.elements;
    for (let i = 0; i < elements.length; i++) {
      const relevantIndex = this.getRelevantIndexOf(elements[i]);
      if (relevantIndex !== -1) {
        this.scaleElementToReframingMode(elements[i], relevantIndex);
      }
    }
    // this.insertPreviewRectangles(videoSource, {
    //   time: 0,
    //   duration: this.store.duration,
    // });
    // }

    return videoSource;
  }

  public restoreVideoSourceForExit(
    videoSourceInput: Record<string, any>,
  ): Record<string, any> {
    if (!this.isModeActive()) {
      this.log(
        'restoreVideoSourceForExit | not active | videoSourceInput',
        videoSourceInput,
      );

      return videoSourceInput;
    }
    this.log(
      'restoreVideoSourceForExit | active | videoSourceInput',
      videoSourceInput,
    );

    if (!this.isStateConsistentForExit(videoSourceInput)) {
      this.logError('restoreVideoSourceForExit | state not consistent');
      return videoSourceInput;
    }

    const videoSource = deepClone(videoSourceInput);

    // if (this.isSimplified()) {
    //   this.log('restoreVideoSourceForExit | simplified');
    //   const elements = videoSource.elements;
    //   for (let i = 0; i < elements.length; i++) {
    //     if (this.isPreviewRectanglesElement(elements[i])) {
    //       this.removePreviewRectanglesAt(i, videoSource);
    //       i--;
    //     }
    //   }
    // } else {
    this.log('restoreVideoSourceForExit | regular');
    this.log(
      'restoreVideoSourceForExit | relevantElementSources',
      this.relevantElementSources,
    );
    videoSource.width = this.actualVideoWidth;
    const elements = videoSource.elements;
    for (let i = 0; i < elements.length; i++) {
      const relevantIndex = this.getRelevantIndexOf(elements[i]);
      if (relevantIndex !== -1) {
        this.scaleElementFromReframingMode(elements[i], relevantIndex);
      } else if (this.isKaraokeElement(elements[i])) {
        // karaoke elements might not be found after refresh because ids have changed
        // need to use config as reference instead of relevantElementSources
        this.scaleRelevantElementFromReframingMode(
          elements[i],
          this.karaokeConfigToRestore!,
        );
      }
      // if (this.isPreviewRectanglesElement(elements[i])) {
      //   this.removePreviewRectanglesAt(i, videoSource);
      //   i--;
      // }
    }
    // }
    this.log('restoreVideoSourceForExit | output | videoSource', videoSource);

    return videoSource;
  }

  public async exitReframingMode() {
    if (!this.isModeActive() && !this._inCriticalSectionForEnter) {
      this.log('exitReframingMode | not active');
      return;
    }

    if (this._inCriticalSectionForExit) {
      this.log('exitReframingMode | in critical section | skip');
      return;
    }

    this.log('exitReframingMode | entering critical section');
    this._inCriticalSectionForExit = true;

    let videoSourceBefore: Record<string, any>;
    try {
      videoSourceBefore = await this.getVideoSource(true);
    } catch (err) {
      this.logError('exitReframingMode | getVideoSource error', err);
      this.actualVideoWidth = null;
      this.rectangleTrack = null;
      this.relevantElementSources = [];
      this.karaokeConfigToRestore = null;
      this._inCriticalSectionForExit = false;
      return;
    }

    const videoSource = this.restoreVideoSourceForExit(videoSourceBefore);
    this.log('exitReframingMode | videoSourceBefore', videoSourceBefore);
    this.log('exitReframingMode | videoSourceAfter', videoSource);

    try {
      await this.setVideoSource(videoSource);
    } catch (err) {
      this.logError('exitReframingMode | setVideoSource error', err);
      try {
        await this.setVideoSourceForExitSafely();
      } catch (err2) {
        this.logError('exitReframingMode | setVideoSourceSafely error', err2);
        return;
      }
    } finally {
      this.log('exitReframingMode | leaving critical section');
      this._inCriticalSectionForExit = false;
    }

    this.actualVideoWidth = null;
    this.rectangleTrack = null;
    this.relevantElementSources = [];

    await this.unselectActiveElement();

    this.store.karaokeProducer!.setConfig(this.restoreKaraokeConfigForExit());
    this.karaokeConfigToRestore = null;

    this.log('exitReframingMode | done');
  }

  private getRelevantIndexOf(elementSource: ElementState['source']): number {
    return this.relevantElementSources.findIndex(
      (el) => el.id === elementSource.id,
    );
  }

  private async unselectActiveElement() {
    try {
      await this.store.setActiveElements();
    } catch (err) {
      this.logError('unselectActiveElement | error', err);
    }
  }

  private scaleElementToReframingMode(
    elementSource: ElementState['source'],
    relevantIndex: number,
  ) {
    const relevantElementSource = this.relevantElementSources[relevantIndex];
    if (!relevantElementSource) {
      return;
    }
    this.scaleRelevantElementToReframingMode(
      elementSource,
      relevantElementSource,
    );
  }

  private scaleRelevantElementToReframingMode(
    elementSource: ElementState['source'],
    relevantElementSource: ElementState['source'],
  ) {
    const prevWidth = parseFloat(relevantElementSource.width || '100');
    const currWidth = this.getInitialScaledWidth(relevantElementSource);

    elementSource.width = `${currWidth}%`;

    const prevX = parseFloat(relevantElementSource.x || '50');
    if (prevX !== 50) {
      const scaledX =
        ((prevX - 50 + (prevWidth / currWidth) * 50) * currWidth) / prevWidth;
      elementSource.x = `${scaledX}%`;
    }
  }

  private getInitialScaledWidth(
    relevantElementSource: ElementState['source'],
  ): number {
    const prevWidth = parseFloat(relevantElementSource.width || '100');
    return (prevWidth * this.actualVideoWidth!) / VIDEO_DEFAULT_WIDTH;
  }

  private scaleElementFromReframingMode(
    elementSource: ElementState['source'],
    relevantIndex: number,
  ) {
    const relevantElementSource = this.relevantElementSources[relevantIndex];
    if (!relevantElementSource) {
      return;
    }
    this.scaleRelevantElementFromReframingMode(
      elementSource,
      relevantElementSource,
    );
  }

  private scaleRelevantElementFromReframingMode(
    elementSource: ElementState['source'],
    relevantElementSource: ElementState['source'],
  ) {
    const currWidth = parseFloat(elementSource.width || '100');
    const prevWidth = parseFloat(relevantElementSource.width || '100');
    const nextWidth =
      (prevWidth * currWidth) /
      this.getInitialScaledWidth(relevantElementSource);

    const currX = parseFloat(elementSource.x || '50');
    const xDiff = (currX - 50) * (nextWidth / currWidth);
    const nextX = 50 + xDiff;

    elementSource.x = `${nextX}%`;
    elementSource.width = `${nextWidth}%`;
  }

  // private insertPreviewRectangles(
  //   videoSource: Record<string, any>,
  //   elementSource: ElementState['source'],
  // ) {
  //   const rectangleWidth = Number(
  //     (VIDEO_DEFAULT_WIDTH - this.actualVideoWidth!) / 2,
  //   ).toFixed(4);
  //   videoSource.elements.push({
  //     ...elementSource,
  //     type: 'composition',
  //     track: this.rectangleTrack,
  //     locked: true,
  //     elements: [
  //       this.makeReframingRectangleElement({
  //         x: rectangleWidth,
  //         width: rectangleWidth,
  //       }),
  //       this.makeReframingRectangleElement({
  //         x: '100%',
  //         width: rectangleWidth,
  //       }),
  //       ...this.makeVerticalDashedLineElements({
  //         x: rectangleWidth,
  //         width: DASH_BREADTH,
  //       }),
  //       ...this.makeVerticalDashedLineElements({
  //         x: Number(
  //           Number(rectangleWidth) + this.actualVideoWidth! - DASH_BREADTH,
  //         ).toFixed(4),
  //         width: DASH_BREADTH,
  //       }),
  //       ...this.makeHorizontalDashedLineElements({
  //         y: 0,
  //         height: DASH_BREADTH,
  //       }).map((el) => {
  //         el.x = Number(el.x) + Number(rectangleWidth);
  //         return el;
  //       }),
  //       ...this.makeHorizontalDashedLineElements({
  //         y: Number(this.getVideoSource().height - DASH_BREADTH * 1.5).toFixed(
  //           4,
  //         ),
  //         height: DASH_BREADTH,
  //       }).map((el) => {
  //         el.x = Number(el.x) + Number(rectangleWidth);
  //         return el;
  //       }),
  //     ].map((el, index) => ({ ...el, track: index + 1 })),
  //   });
  // }

  // private removePreviewRectanglesAt(
  //   index: number,
  //   videoSource: Record<string, any>,
  // ) {
  //   videoSource.elements.splice(index, 1);
  // }

  // public isPreviewRectanglesElement(
  //   elementSource: ElementState['source'],
  // ): boolean {
  //   return (
  //     elementSource.type === 'composition' &&
  //     elementSource.elements?.length >= 4 &&
  //     elementSource.elements.every(
  //       (el: Record<string, any>) => el.type === 'shape',
  //     )
  //   );
  // }

  // private makeReframingRectangleElement(
  //   variableProperties: Record<'x' | 'width', string | number>,
  // ): Record<string, string | number | boolean | null | undefined> {
  //   return {
  //     type: 'shape',
  //     time: 0,
  //     height: '100%',
  //     x_anchor: '100%',
  //     locked: true,
  //     fill_color: 'rgb(3, 4, 26, 0.7)',
  //     path: 'M 0 0 L 100 0 L 100 100 L 0 100 L 0 0 Z',
  //     ...variableProperties,
  //   };
  // }

  // private getDashSize(): number {
  //   return this.getVideoSource().height / (TOTAL_DASHES_VERTICALLY * 2);
  // }

  // private makeVerticalDashedLineElements(
  //   variableProperties: Record<'x' | 'width', string | number>,
  // ): Record<string, string | number | boolean | null | undefined>[] {
  //   const dashHeight = this.getDashSize();
  //   // Need to move the last dash to the very end, to display corners correctly.
  //   const lastY = (TOTAL_DASHES_VERTICALLY - 1) * dashHeight * 2;
  //   const diff = this.getVideoSource().height - (lastY + dashHeight);
  //   // Need to add an offset to every non-corner dash to prevent big gap before last dash
  //   const offset = diff / (TOTAL_DASHES_VERTICALLY - 2);

  //   const result: Record<
  //     string,
  //     string | number | boolean | null | undefined
  //   >[] = [];
  //   for (let i = 0; i < TOTAL_DASHES_VERTICALLY; i++) {
  //     let y; // Move each dash by its height + gap + offset (non-corner dashes)
  //     if (i === TOTAL_DASHES_VERTICALLY - 1) {
  //       y =
  //         this.getVideoSource().height -
  //         dashHeight -
  //         Number(variableProperties.width) / 2; // make corner stick to the bottom
  //     } else {
  //       y = i * dashHeight * 2 + offset * i;
  //     }
  //     result.push({
  //       ...this.makeReframingRectangleElement(variableProperties),
  //       fill_color: '#006FEE',
  //       height: dashHeight,
  //       y,
  //       y_anchor: '0%',
  //       x_anchor: '0%',
  //     });
  //   }
  //   return result;
  // }

  // private makeHorizontalDashedLineElements(
  //   variableProperties: Record<'y' | 'height', string | number>,
  // ): Record<string, string | number | boolean | null | undefined>[] {
  //   // need to make horizontal dash width equal to vertical dash height
  //   const totalDashes = Math.round(
  //     this.actualVideoWidth! /
  //       (this.getVideoSource().height / TOTAL_DASHES_VERTICALLY),
  //   );
  //   const dashWidth = this.getDashSize();
  //   // Need to move the last dash to the very end, to display corners correctly.
  //   const lastX = (totalDashes - 1) * dashWidth * 2;
  //   const diff = this.actualVideoWidth! - (lastX + dashWidth);
  //   // Need to add an offset to every non-corner dash to prevent big gap before last dash
  //   const offset = diff / (totalDashes - 2);

  //   const result: Record<
  //     string,
  //     string | number | boolean | null | undefined
  //   >[] = [];
  //   for (let i = 0; i < totalDashes; i++) {
  //     // Move each dash by its width + gap + offset (non-corner dashes)
  //     let x;
  //     if (i === totalDashes - 1) {
  //       x =
  //         this.actualVideoWidth! -
  //         dashWidth -
  //         Number(variableProperties.height) / 2; // make corner stick to the right
  //     } else {
  //       x = i * dashWidth * 2 + offset * i;
  //     }
  //     result.push({
  //       ...this.makeReframingRectangleElement({
  //         x,
  //         width: dashWidth,
  //       }),
  //       ...variableProperties,
  //       fill_color: '#006FEE',
  //       x_anchor: '0%',
  //       y_anchor: '0%',
  //     });
  //   }
  //   return result;
  // }

  public isModeActive(): boolean {
    return this.rectangleTrack != null;
  }

  private isSimplified(): boolean {
    return this.store.currentVideo!.aspectRatio === AspectRatio.AR_16_9;
  }

  // private insertPreviewRectanglesSimplified(
  //   videoSource: Record<string, any>,
  //   elementSource: ElementState['source'],
  // ) {
  //   videoSource.elements.push({
  //     ...elementSource,
  //     type: 'composition',
  //     track: this.rectangleTrack,
  //     locked: true,
  //     elements: [
  //       ...this.makeVerticalDashedLineElements({
  //         x: 0,
  //         width: DASH_BREADTH,
  //       }),
  //       ...this.makeVerticalDashedLineElements({
  //         x: Number(this.actualVideoWidth! - DASH_BREADTH * 1.5).toFixed(4),
  //         width: DASH_BREADTH,
  //       }),
  //       ...this.makeHorizontalDashedLineElements({
  //         y: 0,
  //         height: DASH_BREADTH,
  //       }),
  //       ...this.makeHorizontalDashedLineElements({
  //         y: Number(this.getVideoSource().height - DASH_BREADTH * 1.5).toFixed(
  //           4,
  //         ),
  //         height: DASH_BREADTH,
  //       }),
  //     ].map((el, index) => ({ ...el, track: index + 1 })),
  //   });
  // }

  public setFlags(flags: Record<string, boolean>) {
    this.flags = flags;
  }

  private log(message: string, data?: unknown, logFn = console.log) {
    try {
      if (data != null) {
        sizeAwareLog(`ReframingModeManager | ${message}`, toJS(data), logFn);
      } else {
        logFn(`ReframingModeManager | ${message}`);
      }
    } catch (err) {
      console.error(`ReframingModeManager | error | ${message}`, err);
    }
  }

  private logError(message: string, data?: unknown) {
    this.log(message, data, console.error);
  }

  // private clearInvisiblePreviewRectangles(
  //   videoSourceInput: Record<string, any>,
  // ): Record<string, any> {
  //   const videoSource = deepClone(videoSourceInput);
  //   const elements = videoSource.elements;
  //   for (let i = 0; i < elements.length; i++) {
  //     if (this.isPreviewRectanglesElement(elements[i])) {
  //       this.removePreviewRectanglesAt(i, videoSource);
  //       i--;
  //     }
  //   }
  //   return videoSource;
  // }

  private async setVideoSourceForExitSafely() {
    const { elements } = await this.getVideoSource();
    const modifications: Record<string, any> = {};

    // if (this.isSimplified()) {
    //   this.log('setVideoSourceForExitSafely | simplified');
    //   for (let i = 0; i < elements.length; i++) {
    //     if (this.isPreviewRectanglesElement(elements[i])) {
    //       // not ideal: no way to delete an element without using setSource
    //       modifications[`${elements[i].id}.visible`] = false;
    //     }
    //   }
    // } else {
    this.log('setVideoSourceForExitSafely | regular');
    modifications.width = this.actualVideoWidth;
    for (let i = 0; i < elements.length; i++) {
      const relevantIndex = this.getRelevantIndexOf(elements[i]);
      if (relevantIndex !== -1) {
        const element = deepClone(elements[i]);
        this.scaleElementFromReframingMode(element, relevantIndex);
        const { id, x, width } = element;
        modifications[`${id}.x`] = x;
        modifications[`${id}.width`] = width;
      } else if (this.isKaraokeElement(elements[i])) {
        // karaoke elements might not be found after refresh because ids have changed
        // need to use config as reference instead of relevantElementSource
        const element = deepClone(elements[i]);
        this.scaleRelevantElementFromReframingMode(
          element,
          this.karaokeConfigToRestore!,
        );
        const { id, x, width } = element;
        modifications[`${id}.x`] = x;
        modifications[`${id}.width`] = width;
      }
      // if (this.isPreviewRectanglesElement(elements[i])) {
      //   modifications[`${elements[i].id}.visible`] = false;
      // }
      // }
    }
    this.log('setVideoSourceForExitSafely | modifications', modifications);

    await this.store.rootStore.videoSourceStore.applyModifications(
      modifications,
    );
  }

  private isStateConsistentForExit(videoSource: Record<string, any>): boolean {
    const hasCorrectVideoWidth = this.isSimplified()
      ? true
      : videoSource.width === VIDEO_DEFAULT_WIDTH;

    // const containsPreviewRectangles = videoSource.elements.some(
    //   (element: ElementState['source']) =>
    //     this.isPreviewRectanglesElement(element) && element.visible !== false,
    // );

    return hasCorrectVideoWidth; //&& containsPreviewRectangles;
  }

  public reset() {
    this.actualVideoWidth = null;
    this.rectangleTrack = null;
    this.relevantElementSources = [];
    this.karaokeConfigToRestore = null;
    this.isToggleAllowed = true;
  }
}

export default ReframingModeManager;
